import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Clock, MessageSquare, RotateCw, Trophy, UserMinus, Ban, Trash2, Pencil, XCircle } from "lucide-react";
import { api } from "../../lib/api";

const RISK_COLORS = {
  emergencial: "bg-red-100 text-red-800 border-red-200",
  alta: "bg-orange-100 text-orange-800 border-orange-200",
  moderada: "bg-amber-100 text-amber-800 border-amber-200",
  programado: "bg-slate-100 text-slate-700 border-slate-200",
};

const relTime = (iso) => {
  if (!iso) return "—";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return `${Math.max(1, Math.floor(diff / 60000))} min atrás`;
    if (h < 48) return `${h}h atrás`;
    return `${Math.floor(h / 24)}d atrás`;
  } catch { return "—"; }
};

const NudgeDialog = ({ open, onClose, consultor, caseObj, onDone }) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setMsg(`Oi ${consultor?.name?.split(" ")[0] || ""}, quer retomar o caso ${caseObj?.protocol || ""} antes de eu reatribuir?`); }, [open, consultor, caseObj]);
  if (!open) return null;
  const send = async () => {
    if (msg.trim().length < 3) { toast.error("Mensagem muito curta"); return; }
    setBusy(true);
    try {
      await api.post("/admin/consultors/nudge", {
        consultor_id: consultor.id, message: msg.trim(), case_id: caseObj?.id,
      });
      toast.success(`Mensagem enviada a ${consultor.name}`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="nudge-dialog">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
        <div className="p-5 border-b border-slate-200">
          <div className="flex items-center gap-2 text-[#0B2B62]">
            <MessageSquare className="w-5 h-5" />
            <div className="font-display font-bold text-lg">Nudge para {consultor?.name}</div>
          </div>
          <div className="text-xs text-slate-500 mt-1">Envia push + e-mail privado. Nada aparece no chat da família.</div>
        </div>
        <div className="p-5 space-y-3">
          {caseObj && (
            <div className="text-xs text-slate-600 bg-slate-50 px-3 py-2 rounded">
              <b>Caso:</b> {caseObj.protocol} · {caseObj.patient_name}
            </div>
          )}
          <textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={5}
            maxLength={500}
            data-testid="nudge-message"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0B2B62]"
            placeholder="Mensagem privada ao consultor…"
          />
          <div className="text-[11px] text-slate-500 text-right">{msg.length}/500</div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancelar</button>
          <button onClick={send} disabled={busy} data-testid="nudge-send" className="px-4 py-2 text-sm bg-[#0B2B62] text-white rounded-md font-semibold disabled:opacity-40 hover:bg-slate-800">
            {busy ? "Enviando…" : "Enviar nudge"}
          </button>
        </div>
      </div>
    </div>
  );
};

const RankingCard = ({ ranking }) => {
  if (!ranking?.length) return null;
  const withHistory = ranking.filter((r) => r.has_history);
  const withoutHistory = ranking.filter((r) => !r.has_history);
  return (
    <div className="border border-slate-200 rounded-xl bg-white" data-testid="sla-ranking-card">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <Trophy className="w-4 h-4 text-amber-600" />
        <div className="font-display font-bold text-[#0B2B62]">Ranking SLA — quem menos perde casos</div>
        <div className="text-[11px] text-slate-500 ml-auto">Menor % = melhor</div>
      </div>
      <div className="divide-y divide-slate-100">
        {withHistory.map((r, i) => {
          const rankColor = i === 0 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                          : i === 1 ? "text-blue-700 bg-blue-50 border-blue-200"
                          : i === 2 ? "text-amber-700 bg-amber-50 border-amber-200"
                          : "text-slate-600 bg-slate-50 border-slate-200";
          const rateColor = r.sla_rate < 20 ? "text-emerald-700"
                          : r.sla_rate < 50 ? "text-amber-700" : "text-red-700";
          return (
            <div key={r.consultor_id} className="px-4 py-2.5 flex items-center gap-3 text-sm" data-testid={`sla-rank-row-${i}`}>
              <div className={`w-7 h-7 rounded-full border flex items-center justify-center text-xs font-bold ${rankColor}`}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-800 truncate flex items-center gap-1.5">
                  {r.name}
                  {r.on_call && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Online agora" />}
                </div>
                <div className="text-[11px] text-slate-500">
                  {r.active_now} ativos · {r.completed} concluídos · {r.sla_lost} perdidos
                </div>
              </div>
              <div className={`font-display font-bold text-lg tabular-nums ${rateColor}`}>
                {r.sla_rate}%
              </div>
            </div>
          );
        })}
        {withoutHistory.length > 0 && (
          <div className="px-4 py-2 text-[11px] text-slate-400 italic">
            + {withoutHistory.length} consultor(es) sem histórico
          </div>
        )}
      </div>
    </div>
  );
};

const RISK_LEVELS = [
  { v: "emergencial", l: "Emergencial" },
  { v: "alta", l: "Alta" },
  { v: "moderada", l: "Moderada" },
  { v: "programado", l: "Programado" },
];

export const CaseEditModal = ({ open, caseObj, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !caseObj) return;
    setForm({
      patient_name: caseObj.patient_name || "",
      patient_age: caseObj.patient_age || "",
      city: caseObj.city || "",
      state: caseObj.state || "",
      risk_level: caseObj.risk_level || "",
      family_phone: caseObj.family_phone || "",
      family_email: caseObj.family_email || "",
      clinical_summary: caseObj.clinical_summary || "",
    });
  }, [open, caseObj]);

  if (!open) return null;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!(form.patient_name || "").trim()) { toast.error("Nome do paciente é obrigatório"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.patient_age === "" || payload.patient_age == null) delete payload.patient_age;
      else payload.patient_age = Number(payload.patient_age);
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.patch(`/admin/cases/${caseObj.id}`, payload);
      toast.success(`Caso ${caseObj.protocol} atualizado.`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar caso.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="case-edit-modal">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#0B2B62]">
            <Pencil className="w-5 h-5" />
            <div>
              <div className="font-display font-bold text-lg">Editar caso</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-widest">{caseObj.protocol}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <label className="block">
            <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Paciente *</span>
            <input value={form.patient_name || ""} onChange={(e) => set("patient_name", e.target.value)}
              data-testid="case-edit-patient-name"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block col-span-2">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Cidade</span>
              <input value={form.city || ""} onChange={(e) => set("city", e.target.value)}
                data-testid="case-edit-city"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">UF</span>
              <input value={form.state || ""} onChange={(e) => set("state", e.target.value.toUpperCase())}
                maxLength={2} data-testid="case-edit-state"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm uppercase" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Idade</span>
              <input type="number" min="0" max="130" value={form.patient_age || ""}
                onChange={(e) => set("patient_age", e.target.value)}
                data-testid="case-edit-age"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Nível de risco</span>
              <select value={form.risk_level || ""} onChange={(e) => set("risk_level", e.target.value)}
                data-testid="case-edit-risk"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                <option value="">—</option>
                {RISK_LEVELS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Telefone família</span>
              <input value={form.family_phone || ""} onChange={(e) => set("family_phone", e.target.value)}
                data-testid="case-edit-phone"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">E-mail família</span>
              <input type="email" value={form.family_email || ""}
                onChange={(e) => set("family_email", e.target.value)}
                data-testid="case-edit-email"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Resumo clínico</span>
            <textarea value={form.clinical_summary || ""}
              onChange={(e) => set("clinical_summary", e.target.value)}
              rows={3} maxLength={2000}
              data-testid="case-edit-summary"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </label>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancelar</button>
          <button onClick={save} disabled={busy}
            data-testid="case-edit-save"
            className="px-4 py-2 text-sm bg-[#0B2B62] text-white rounded-md font-semibold disabled:opacity-40 hover:bg-slate-800 inline-flex items-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" /> {busy ? "Salvando…" : "Salvar alterações"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CANCEL_REASONS = [
  { v: "duplicado", l: "Duplicado" },
  { v: "trote", l: "Trote / spam" },
  { v: "solicitacao_familia", l: "Solicitação da família" },
  { v: "sem_contato", l: "Sem contato" },
  { v: "erro_cadastral", l: "Erro cadastral" },
  { v: "resolvido_externo", l: "Resolvido fora da RAF" },
  { v: "outro", l: "Outro (descrever)" },
];

const CancelCaseDialog = ({ open, caseObj, onClose, onDone }) => {
  const [reasonKey, setReasonKey] = useState("duplicado");
  const [freeText, setFreeText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setReasonKey("duplicado"); setFreeText(""); } }, [open]);
  if (!open) return null;

  const confirm = async () => {
    const meta = CANCEL_REASONS.find((r) => r.v === reasonKey);
    let reason = meta?.l || reasonKey;
    if (reasonKey === "outro") {
      if (freeText.trim().length < 3) { toast.error("Descreva o motivo (mín. 3 caracteres)"); return; }
      reason = freeText.trim();
    } else if (freeText.trim()) {
      reason = `${meta?.l}: ${freeText.trim()}`;
    }
    setBusy(true);
    try {
      await api.post(`/admin/cases/${caseObj.id}/cancel`, { reason });
      toast.success(`Caso ${caseObj.protocol} cancelado.`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar caso");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cancel-reason-modal">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#0B2B62]">
            <Ban className="w-5 h-5" />
            <div>
              <div className="font-display font-bold text-lg">Cancelar caso</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-widest">{caseObj.protocol} · {caseObj.patient_name}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Motivo do cancelamento *</span>
            <select value={reasonKey} onChange={(e) => setReasonKey(e.target.value)}
              data-testid="cancel-reason-select"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
              {CANCEL_REASONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">
              {reasonKey === "outro" ? "Descrição *" : "Observação (opcional)"}
            </span>
            <textarea value={freeText} onChange={(e) => setFreeText(e.target.value)}
              rows={3} maxLength={500}
              placeholder={reasonKey === "outro" ? "Descreva o motivo em detalhes…" : "Detalhes adicionais que ajudam a auditoria (opcional)"}
              data-testid="cancel-reason-freetext"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </label>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Voltar</button>
          <button onClick={confirm} disabled={busy}
            data-testid="cancel-reason-confirm"
            className="px-4 py-2 text-sm bg-slate-800 text-white rounded-md font-semibold disabled:opacity-40 hover:bg-slate-900 inline-flex items-center gap-1.5">
            <Ban className="w-3.5 h-3.5" /> {busy ? "Cancelando…" : "Confirmar cancelamento"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReopenedCasesPanel = () => {
  const [data, setData] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedByCase, setSelectedByCase] = useState({});
  const [reassigning, setReassigning] = useState(null);
  const [nudgeCtx, setNudgeCtx] = useState(null); // {consultor, caseObj}
  const [editingCase, setEditingCase] = useState(null);
  const [cancellingCase, setCancellingCase] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reopened, rank] = await Promise.all([
        api.get("/admin/reopened-cases"),
        api.get("/admin/sla-ranking"),
      ]);
      setData(reopened.data);
      setRanking(rank.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar reaberturas");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const consultorsMap = useMemo(() => {
    const m = {};
    (data?.all_consultors || []).forEach((u) => { m[u.id] = u; });
    return m;
  }, [data]);

  const reassign = async (caseObj) => {
    const consultor_id = selectedByCase[caseObj.id];
    if (!consultor_id) { toast.error("Selecione um consultor antes de reatribuir"); return; }
    const consultor = consultorsMap[consultor_id];
    if (!window.confirm(`Reatribuir ${caseObj.protocol} — ${caseObj.patient_name} → ${consultor?.name}?`)) return;
    setReassigning(caseObj.id);
    try {
      const { data: r } = await api.post(`/admin/cases/${caseObj.id}/reassign`, { consultor_id });
      const famPart = r.family_notified ? " · família notificada" : "";
      toast.success(`Caso reatribuído a ${r.consultor_name}${famPart}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao reatribuir");
    } finally { setReassigning(null); }
  };

  const cancelCase = (caseObj) => setCancellingCase(caseObj);

  const deleteCase = async (caseObj) => {
    if (!window.confirm(
      `EXCLUIR DEFINITIVAMENTE ${caseObj.protocol} — ${caseObj.patient_name}?\n\n` +
      "Essa ação apaga o registro do caso. Use \"Cancelar\" pra preservar o histórico.",
    )) return;
    try {
      await api.delete(`/admin/cases/${caseObj.id}`);
      toast.success(`Caso ${caseObj.protocol} excluído.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir caso");
    }
  };

  if (!data && loading) return <div className="p-8 text-slate-500 text-sm">Carregando reaberturas SLA-48h…</div>;
  if (!data) return null;

  const online = data.online_consultors || [];
  const all = data.all_consultors || [];

  return (
    <div className="space-y-5" data-testid="reopened-cases-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h2 className="font-display font-bold text-xl text-[#0B2B62]">Reaberturas SLA-48h</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Casos que voltaram automaticamente ao pool porque o consultor designado passou 48h sem concluir o encaminhamento.
            Mande um nudge privado antes ou reatribua a um colega online — a família também recebe push.
          </p>
        </div>
        <button onClick={load} data-testid="reopened-refresh"
          className="text-xs px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 inline-flex items-center gap-1.5">
          <RotateCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat label="Casos reabertos" value={data.total} accent />
        <MiniStat label="Consultores online" value={online.length} />
        <MiniStat label="Total consultores" value={all.length} />
        <MiniStat label="Cron SLA" value="48h" muted />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-3">
          {data.total === 0 ? (
            <div className="p-8 border border-dashed border-emerald-300 rounded-xl bg-emerald-50 text-emerald-800 text-sm text-center" data-testid="reopened-empty">
              Nenhum caso pendente de reatribuição. Todos os consultores estão dentro do prazo SLA-48h.
            </div>
          ) : (
            <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                <div className="col-span-4">Caso / Família</div>
                <div className="col-span-2">Risco</div>
                <div className="col-span-2">Anterior</div>
                <div className="col-span-2">Reaberto</div>
                <div className="col-span-2 text-right">Ações</div>
              </div>
              {data.cases.map((c) => (
                <div key={c.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-slate-100 items-center text-sm" data-testid={`reopened-row-${c.protocol}`}>
                  <div className="col-span-4">
                    <div className="font-semibold text-[#0B2B62]">{c.protocol}</div>
                    <div className="text-slate-700 truncate">{c.patient_name}</div>
                    <div className="text-[11px] text-slate-500">{c.city}{c.state ? ` / ${c.state}` : ""}</div>
                  </div>
                  <div className="col-span-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-widest font-semibold ${RISK_COLORS[c.risk_level] || RISK_COLORS.programado}`}>
                      {c.risk_level || "—"}
                    </span>
                  </div>
                  <div className="col-span-2 text-slate-700 truncate">
                    <UserMinus className="w-3.5 h-3.5 inline-block mr-1 text-red-500" />
                    {c.prev_consultant_name || "—"}
                  </div>
                  <div className="col-span-2 text-slate-600 text-xs">
                    <Clock className="w-3.5 h-3.5 inline-block mr-1 text-amber-600" />
                    {relTime(c.sla_reopened_at)}
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1.5 flex-wrap">
                    <select
                      data-testid={`reopened-select-${c.protocol}`}
                      value={selectedByCase[c.id] || ""}
                      onChange={(e) => setSelectedByCase((prev) => ({ ...prev, [c.id]: e.target.value }))}
                      className="px-2 py-1.5 border border-slate-300 rounded-md text-xs bg-white max-w-[130px]"
                    >
                      <option value="">Consultor…</option>
                      {online.length > 0 && (
                        <optgroup label="Online agora">
                          {online.filter((u) => u.id !== c.prev_consultant_id).map((u) => (
                            <option key={u.id} value={u.id}>🟢 {u.name}</option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label="Todos">
                        {all.filter((u) => u.id !== c.prev_consultant_id && !online.find((o) => o.id === u.id)).map((u) => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </optgroup>
                    </select>
                    {c.prev_consultant_id && (
                      <button
                        onClick={() => setNudgeCtx({
                          consultor: { id: c.prev_consultant_id, name: c.prev_consultant_name },
                          caseObj: c,
                        })}
                        data-testid={`reopened-nudge-${c.protocol}`}
                        title="Mandar mensagem privada ao consultor anterior antes de reatribuir"
                        className="text-xs px-2 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 inline-flex items-center gap-1"
                      >
                        <MessageSquare className="w-3.5 h-3.5" /> Nudge
                      </button>
                    )}
                    <button
                      onClick={() => reassign(c)}
                      disabled={!selectedByCase[c.id] || reassigning === c.id}
                      data-testid={`reopened-reassign-${c.protocol}`}
                      className="text-xs px-3 py-1.5 bg-[#0B2B62] text-white rounded-md font-semibold disabled:opacity-40 hover:bg-slate-800"
                    >
                      {reassigning === c.id ? "…" : "Reatribuir"}
                    </button>
                    <button
                      onClick={() => setEditingCase(c)}
                      data-testid={`reopened-edit-${c.protocol}`}
                      title="Editar dados do caso"
                      className="text-xs px-2 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 inline-flex items-center gap-1 text-slate-700"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Editar
                    </button>
                    <button
                      onClick={() => cancelCase(c)}
                      data-testid={`reopened-cancel-${c.protocol}`}
                      title="Cancelar o caso — preserva o registro no histórico"
                      className="text-xs px-2 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 inline-flex items-center gap-1 text-slate-700"
                    >
                      <Ban className="w-3.5 h-3.5" /> Cancelar
                    </button>
                    <button
                      onClick={() => deleteCase(c)}
                      data-testid={`reopened-delete-${c.protocol}`}
                      title="Excluir o caso definitivamente"
                      className="text-xs px-2 py-1.5 border border-red-200 rounded-md hover:bg-red-50 inline-flex items-center gap-1 text-red-600"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Excluir
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <RankingCard ranking={ranking?.ranking} />
        </div>
      </div>

      <NudgeDialog
        open={!!nudgeCtx}
        onClose={() => setNudgeCtx(null)}
        consultor={nudgeCtx?.consultor}
        caseObj={nudgeCtx?.caseObj}
        onDone={load}
      />

      <CaseEditModal
        open={!!editingCase}
        caseObj={editingCase}
        onClose={() => setEditingCase(null)}
        onSaved={load}
      />

      <CancelCaseDialog
        open={!!cancellingCase}
        caseObj={cancellingCase}
        onClose={() => setCancellingCase(null)}
        onDone={load}
      />
    </div>
  );
};

const MiniStat = ({ label, value, accent, muted }) => (
  <div className={`p-3 border rounded-lg ${accent ? "border-amber-300 bg-amber-50" : muted ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`}>
    <div className={`text-[10px] uppercase tracking-widest ${accent ? "text-amber-700" : "text-slate-500"} font-semibold`}>{label}</div>
    <div className={`font-display font-bold text-2xl mt-1 ${accent ? "text-amber-800" : "text-[#0B2B62]"}`}>{value}</div>
  </div>
);

export default ReopenedCasesPanel;
